import React, { useState, useEffect } from 'react';
import { Plus, Edit2, Trash2, Search, X, Package, Filter } from 'lucide-react';
import apiClient from '../services/api';
import '../styles/MasterForms.css';

const Items = () => {
  const getSessionData = () => {
    try {
      const sessionStr = localStorage.getItem('ltms_session');
      if (!sessionStr) return null;
      
      const sessionData = JSON.parse(sessionStr);
      return sessionData?.session || sessionData;
    } catch (error) {
      console.error('Error parsing session data:', error);
      return null;
    }
  };

  const session = getSessionData();

  const [items, setItems] = useState([]);
  const [filteredItems, setFilteredItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [showModal, setShowModal] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [formData, setFormData] = useState({
    itemid: null,
    itemcode: '',
    itemname: '',
    itemtype: '',
    accountheadid: null,
    taxrate: 0,
    isactive: true
  });
  const [errors, setErrors] = useState({});
  const [submitLoading, setSubmitLoading] = useState(false);

  // Item type options
  const itemTypes = [
    'Product',
    'Service',
    'Raw Material',
    'Finished Good',
    'Consumable',
    'Trading Good',
    'Fixed Asset',
    'Other'
  ];

  useEffect(() => {
    fetchItems();
  }, []);

  useEffect(() => {
    filterItems();
  }, [searchTerm, filterType, filterStatus, items]);

  const fetchItems = async () => {
    setLoading(true);
    try {
      const response = await apiClient.get('/Items.php');
      
      if (response.data.success) {
        setItems(response.data.data || []);
      }
    } catch (error) {
      console.error('Error fetching items:', error);
      if (error.response?.status === 401) {
        alert('Session expired. Please login again.');
      }
    } finally {
      setLoading(false);
    }
  };

  const filterItems = () => {
    let filtered = items;

    // Search filter
    if (searchTerm.trim()) {
      filtered = filtered.filter(item =>
        item.itemname.toLowerCase().includes(searchTerm.toLowerCase()) ||
        item.itemcode.toLowerCase().includes(searchTerm.toLowerCase()) ||
        item.itemtype?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    // Type filter
    if (filterType) {
      filtered = filtered.filter(item => item.itemtype === filterType);
    }

    // Status filter
    if (filterStatus !== 'all') {
      const isActive = filterStatus === 'active';
      filtered = filtered.filter(item => item.isactive === (isActive ? 1 : 0));
    }

    setFilteredItems(filtered);
  };

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.itemcode.trim()) {
      newErrors.itemcode = 'Item code is required';
    } else if (formData.itemcode.length > 50) {
      newErrors.itemcode = 'Item code must be 50 characters or less';
    }
    
    if (!formData.itemname.trim()) {
      newErrors.itemname = 'Item name is required';
    } else if (formData.itemname.length > 200) {
      newErrors.itemname = 'Item name must be 200 characters or less';
    }
    
    if (formData.itemtype && formData.itemtype.length > 50) {
      newErrors.itemtype = 'Item type must be 50 characters or less';
    }
    
    if (formData.taxrate < 0 || formData.taxrate > 100) {
      newErrors.taxrate = 'Tax rate must be between 0 and 100';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }
    
    setSubmitLoading(true);
    try {
      const method = editMode ? 'put' : 'post';
      const response = await apiClient[method]('/Items.php', {
        ...formData,
        accountheadid: formData.accountheadid || null,
        taxrate: parseFloat(formData.taxrate) || 0,
        isactive: formData.isactive ? 1 : 0
      });
      
      const data = response.data;
      
      if (data.success) {
        alert(editMode ? 'Item updated successfully!' : 'Item created successfully!');
        setShowModal(false);
        resetForm();
        fetchItems();
      } else {
        alert('Error: ' + data.message);
      }
    } catch (error) {
      console.error('Error saving item:', error);
      if (error.response?.status === 401) {
        alert('Session expired. Please login again.');
      } else if (error.response?.status === 409) {
        alert('Item code already exists. Please use a different code.');
      } else {
        alert('Error saving item. Please try again.');
      }
    } finally {
      setSubmitLoading(false);
    }
  };

  const handleEdit = (item) => {
    setFormData({
      itemid: item.itemid,
      itemcode: item.itemcode,
      itemname: item.itemname,
      itemtype: item.itemtype || '',
      accountheadid: item.accountheadid || null,
      taxrate: parseFloat(item.taxrate) || 0,
      isactive: item.isactive === 1
    });
    setEditMode(true);
    setShowModal(true);
    setErrors({});
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this item?')) {
      return;
    }
    
    try {
      const response = await apiClient.delete('/Items.php', {
        data: { itemid: id }
      });
      
      const data = response.data;
      
      if (data.success) {
        alert('Item deleted successfully!');
        fetchItems();
      } else {
        alert('Error: ' + data.message);
      }
    } catch (error) {
      console.error('Error deleting item:', error);
      if (error.response?.status === 401) {
        alert('Session expired. Please login again.');
      } else {
        alert('Error deleting item. Please try again.');
      }
    }
  };

  const resetForm = () => {
    setFormData({
      itemid: null,
      itemcode: '',
      itemname: '',
      itemtype: '',
      accountheadid: null,
      taxrate: 0,
      isactive: true
    });
    setEditMode(false);
    setErrors({});
  };

  const openAddModal = () => {
    resetForm();
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    resetForm();
  };

  const clearFilters = () => {
    setSearchTerm('');
    setFilterType('');
    setFilterStatus('all');
  };

  return (
    <div className="master-container">
      <div className="master-header">
        <div className="header-left">
          <h2>
            <Package size={32} style={{ display: 'inline', marginRight: '10px', verticalAlign: 'middle' }} />
            Items Master
          </h2>
          <p className="subtitle">Manage items, products, and services</p>
        </div>
        <button className="btn-primary" onClick={openAddModal}>
          <Plus size={20} />
          Add Item
        </button>
      </div>

      <div className="master-toolbar">
        <div className="search-box">
          <Search size={20} />
          <input
            type="text"
            placeholder="Search by item code, name, or type..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
          {searchTerm && (
            <button className="btn-clear" onClick={() => setSearchTerm('')}>
              <X size={16} />
            </button>
          )}
        </div>

        <div className="filter-group">
          <select
            className="filter-select"
            value={filterType}
            onChange={(e) => setFilterType(e.target.value)}
          >
            <option value="">All Types</option>
            {itemTypes.map(type => (
              <option key={type} value={type}>{type}</option>
            ))}
          </select>

          <select
            className="filter-select"
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
          >
            <option value="all">All Status</option>
            <option value="active">Active Only</option>
            <option value="inactive">Inactive Only</option>
          </select>

          {(searchTerm || filterType || filterStatus !== 'all') && (
            <button 
              className="btn-secondary" 
              onClick={clearFilters}
              style={{ padding: '10px 16px' }}
            >
              <X size={16} />
              Clear
            </button>
          )}
        </div>

        <div className="toolbar-info">
          Total: {filteredItems.length} {filteredItems.length === 1 ? 'item' : 'items'}
        </div>
      </div>

      {loading ? (
        <div className="loading-state">Loading items...</div>
      ) : filteredItems.length === 0 ? (
        <div className="empty-state">
          <p>No items found</p>
          {(searchTerm || filterType || filterStatus !== 'all') && (
            <button onClick={clearFilters}>Clear filters</button>
          )}
        </div>
      ) : (
        <div className="table-container" style={{ overflowX: 'auto' }}>
          <table className="data-table" style={{ minWidth: '100%', tableLayout: 'auto' }}>
            <thead>
              <tr>
                <th style={{ minWidth: '120px' }}>Item Code</th>
                <th style={{ minWidth: '200px' }}>Item Name</th>
                <th style={{ minWidth: '120px' }}>Type</th>
                <th style={{ minWidth: '100px', textAlign: 'right' }}>Tax Rate (%)</th>
                <th style={{ minWidth: '100px' }}>Status</th>
                <th style={{ minWidth: '120px' }}>Created At</th>
                <th style={{ 
                  minWidth: '140px', 
                  textAlign: 'center', 
                  position: 'sticky', 
                  right: 0, 
                  backgroundColor: '#764ba2',
                  zIndex: 10
                }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredItems.map((item) => (
                <tr key={item.itemid}>
                  <td className="font-mono">{item.itemcode}</td>
                  <td className="font-semibold">{item.itemname}</td>
                  <td>
                    {item.itemtype ? (
                      <span className="type-badge">
                        {item.itemtype}
                      </span>
                    ) : (
                      <span style={{ color: '#9CA3AF', fontStyle: 'italic' }}>-</span>
                    )}
                  </td>
                  <td style={{ textAlign: 'right', fontFamily: 'monospace', fontWeight: '600' }}>
                    {parseFloat(item.taxrate).toFixed(2)}%
                  </td>
                  <td>
                    <span className={`status-badge ${item.isactive ? 'active' : 'inactive'}`}>
                      {item.isactive ? 'Active' : 'Inactive'}
                    </span>
                  </td>
                  <td>{new Date(item.createdat).toLocaleDateString()}</td>
                  <td style={{ 
                    textAlign: 'center', 
                    whiteSpace: 'nowrap',
                    position: 'sticky',
                    right: 0,
                    backgroundColor: 'white',
                    boxShadow: '-4px 0 6px -2px rgba(0, 0, 0, 0.1)',
                    zIndex: 5
                  }}>
                    <button
                      onClick={() => handleEdit(item)}
                      title="Edit"
                      style={{
                        padding: '8px 12px',
                        marginRight: '8px',
                        border: '2px solid #667eea',
                        borderRadius: '8px',
                        backgroundColor: '#ede9fe',
                        color: '#667eea',
                        cursor: 'pointer',
                        display: 'inline-flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        transition: 'all 0.2s ease',
                        fontWeight: '600'
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.backgroundColor = '#667eea';
                        e.currentTarget.style.color = '#ffffff';
                        e.currentTarget.style.transform = 'scale(1.05) translateY(-2px)';
                        e.currentTarget.style.boxShadow = '0 4px 12px rgba(102, 126, 234, 0.3)';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.backgroundColor = '#ede9fe';
                        e.currentTarget.style.color = '#667eea';
                        e.currentTarget.style.transform = 'scale(1) translateY(0)';
                        e.currentTarget.style.boxShadow = 'none';
                      }}
                    >
                      <Edit2 size={16} />
                    </button>
                    <button
                      onClick={() => handleDelete(item.itemid)}
                      title="Delete"
                      style={{
                        padding: '8px 12px',
                        border: '2px solid #dc2626',
                        borderRadius: '8px',
                        backgroundColor: '#fee2e2',
                        color: '#dc2626',
                        cursor: 'pointer',
                        display: 'inline-flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        transition: 'all 0.2s ease',
                        fontWeight: '600'
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.backgroundColor = '#dc2626';
                        e.currentTarget.style.color = '#ffffff';
                        e.currentTarget.style.transform = 'scale(1.05) translateY(-2px)';
                        e.currentTarget.style.boxShadow = '0 4px 12px rgba(220, 38, 38, 0.3)';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.backgroundColor = '#fee2e2';
                        e.currentTarget.style.color = '#dc2626';
                        e.currentTarget.style.transform = 'scale(1) translateY(0)';
                        e.currentTarget.style.boxShadow = 'none';
                      }}
                    >
                      <Trash2 size={16} />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {showModal && (
        <div className="modal-overlay" onClick={closeModal}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>{editMode ? 'Edit Item' : 'Add New Item'}</h3>
              <button className="btn-close" onClick={closeModal}>
                <X size={24} />
              </button>
            </div>
            
            <form onSubmit={handleSubmit} className="modal-form">
              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="itemcode">
                    Item Code <span className="required">*</span>
                  </label>
                  <input
                    type="text"
                    id="itemcode"
                    value={formData.itemcode}
                    onChange={(e) => setFormData({ ...formData, itemcode: e.target.value.toUpperCase() })}
                    maxLength="50"
                    placeholder="e.g., PROD001, SVC001"
                    className={errors.itemcode ? 'error' : ''}
                    disabled={editMode}
                  />
                  {errors.itemcode && (
                    <span className="error-message">{errors.itemcode}</span>
                  )}
                  <small style={{ color: '#6B7280', marginTop: '4px', display: 'block' }}>
                    Unique identifier for the item
                  </small>
                </div>

                <div className="form-group">
                  <label htmlFor="itemname">
                    Item Name <span className="required">*</span>
                  </label>
                  <input
                    type="text"
                    id="itemname"
                    value={formData.itemname}
                    onChange={(e) => setFormData({ ...formData, itemname: e.target.value })}
                    maxLength="200"
                    placeholder="e.g., Premium Widget, Consulting Service"
                    className={errors.itemname ? 'error' : ''}
                  />
                  {errors.itemname && (
                    <span className="error-message">{errors.itemname}</span>
                  )}
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="itemtype">Item Type</label>
                  <select
                    id="itemtype"
                    value={formData.itemtype}
                    onChange={(e) => setFormData({ ...formData, itemtype: e.target.value })}
                    className={errors.itemtype ? 'error' : ''}
                  >
                    <option value="">-- Select Type --</option>
                    {itemTypes.map(type => (
                      <option key={type} value={type}>{type}</option>
                    ))}
                  </select>
                  {errors.itemtype && (
                    <span className="error-message">{errors.itemtype}</span>
                  )}
                  <small style={{ color: '#6B7280', marginTop: '4px', display: 'block' }}>
                    Category of the item
                  </small>
                </div>

                <div className="form-group">
                  <label htmlFor="taxrate">Tax Rate (%)</label>
                  <input
                    type="number"
                    id="taxrate"
                    value={formData.taxrate}
                    onChange={(e) => setFormData({ ...formData, taxrate: parseFloat(e.target.value) || 0 })}
                    min="0"
                    max="100"
                    step="0.01"
                    placeholder="0.00"
                    className={errors.taxrate ? 'error' : ''}
                  />
                  {errors.taxrate && (
                    <span className="error-message">{errors.taxrate}</span>
                  )}
                  <small style={{ color: '#6B7280', marginTop: '4px', display: 'block' }}>
                    Applicable tax rate (0-100)
                  </small>
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="accountheadid">Account Head ID</label>
                  <input
                    type="number"
                    id="accountheadid"
                    value={formData.accountheadid || ''}
                    onChange={(e) => setFormData({ ...formData, accountheadid: e.target.value ? parseInt(e.target.value) : null })}
                    placeholder="Optional"
                  />
                  <small style={{ color: '#6B7280', marginTop: '4px', display: 'block' }}>
                    Link to accounting head (optional)
                  </small>
                </div>

                <div className="form-group">
                  <label className="checkbox-label">
                    <input
                      type="checkbox"
                      checked={formData.isactive}
                      onChange={(e) => setFormData({ ...formData, isactive: e.target.checked })}
                    />
                    <span>Active</span>
                  </label>
                </div>
              </div>

              <div className="modal-footer">
                <button
                  type="button"
                  className="btn-secondary"
                  onClick={closeModal}
                  disabled={submitLoading}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="btn-primary"
                  disabled={submitLoading}
                >
                  {submitLoading ? 'Saving...' : (editMode ? 'Update' : 'Create')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Items;